import React, { useState, useEffect } from 'react';
import API_CONFIG from '../../apiConfig';
import CustomSelect from '../ui/CustomSelect';

interface TemplateSelectionProps {
  selectedTemplateIds: string[];
  setSelectedTemplateIds: React.Dispatch<React.SetStateAction<string[]>>;
  onTemplateSelect?: (templateUrl: string, templateId: string, templatePrompt?: string) => void;
  selectedGender?: string;
  setSelectedGender?: (gender: string) => void;
  maxHeight?: string; // Optional max height for template grid (default: 600px)
}

interface Template {
  id: string;
  name: string;
  imageUrl: string;
  description: string;
  category: string;
  prompt?: string;
}

// Default random template
const RANDOM_TEMPLATE: Template = {
    id: 'random',
    name: 'Random Template',
    imageUrl: '',
    description: 'System will choose automatically',
    category: 'Random'
};

const TemplateSelection: React.FC<TemplateSelectionProps> = ({ 
  selectedTemplateIds, 
  setSelectedTemplateIds, 
  onTemplateSelect,
  selectedGender = 'female',
  setSelectedGender,
  maxHeight = '600px'
}) => {
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [templates, setTemplates] = useState<Template[]>([RANDOM_TEMPLATE]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch templates from API
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(API_CONFIG.REALISTIC_TRYON_TEMPLATES_URL, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch templates: ${response.statusText}`);
        }

        const data = await response.json();
        
        // Map API response to Template interface
        const fetchedTemplates: Template[] = Array.isArray(data) 
          ? data.map((item: any) => ({
              id: item.templateId || item.id,
              name: item.templateName || item.name || `Template ${item.templateId}`,
              imageUrl: item.templateUrl || item.imageUrl || '',
              description: item.templateDescription || item.description || '',
              category: item.category || item.templateName || 'General',
              prompt: item.prompt || ''
            }))
          : [];
        
        // Sort templates by templateId (numeric sort)
        const sortedTemplates = fetchedTemplates.sort((a, b) => {
          const numA = parseInt(a.id, 10);
          const numB = parseInt(b.id, 10);
          return numA - numB;
        });
        
        // Add random template at the beginning
        setTemplates([RANDOM_TEMPLATE, ...sortedTemplates]);
      } catch (err) {
        console.error('Error fetching templates:', err);
        setError(err instanceof Error ? err.message : 'Failed to load templates');
        // Keep only random template on error
        setTemplates([RANDOM_TEMPLATE]);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  const handleTemplateSelect = (template: Template) => {
    const hasRandomSelected = selectedTemplateIds.includes('random');
    const hasNonRandomSelected = selectedTemplateIds.some(id => id !== 'random');

    if (template.id === 'random') {
      if (hasNonRandomSelected) {
        // Selecting Random while others are selected: clear others and keep only Random
        if (onTemplateSelect) {
          selectedTemplateIds
            .filter(id => id !== 'random')
            .forEach(id => {
              const t = templates.find(tt => tt.id === id);
              if (t) onTemplateSelect(t.imageUrl, t.id, t.prompt);
            });
        }
        setSelectedTemplateIds(['random']);
        if (onTemplateSelect) {
          onTemplateSelect(template.imageUrl, template.id, template.prompt);
        }
      } else {
        // Toggle random normally
        setSelectedTemplateIds(prev => (
          prev.includes('random') ? prev.filter(id => id !== 'random') : ['random']
        ));
        if (onTemplateSelect) {
          onTemplateSelect(template.imageUrl, template.id, template.prompt);
        }
      }
      return;
    }

    // Non-random template selected
    if (hasRandomSelected) {
      // Deselect Random first
      const randomTemplate = templates.find(t => t.id === 'random');
      if (onTemplateSelect && randomTemplate) {
        onTemplateSelect(randomTemplate.imageUrl, randomTemplate.id, randomTemplate.prompt);
      }
      setSelectedTemplateIds(prev => {
        const base = prev.filter(id => id !== 'random');
        if (base.includes(template.id)) return base.filter(id => id !== template.id);
        return [...base, template.id];
      });
      if (onTemplateSelect) {
        onTemplateSelect(template.imageUrl, template.id, template.prompt);
      }
      return;
    }

    // Normal toggle for non-random when Random isn't selected
    setSelectedTemplateIds(prev => (
      prev.includes(template.id) ? prev.filter(id => id !== template.id) : [...prev, template.id]
    ));
    if (onTemplateSelect) {
      onTemplateSelect(template.imageUrl, template.id, template.prompt);
    }
  };

  // Get unique categories
  const categories = ['all', ...Array.from(new Set(templates.map(t => t.category)))];

  // Filter templates based on category
  const filteredTemplates = categoryFilter === 'all' 
    ? templates 
    : templates.filter(t => t.category === categoryFilter);

  return (
    <div className="bg-accent-sand rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-text-heading">
          Templates 
          <span className="ml-2 text-sm font-normal text-text-description">
            ({filteredTemplates.length} / {templates.length})
          </span>
        </h3>
        
        {/* Gender and Category Filters */}
        <div className="flex items-center space-x-2 mr-5">
          {/* Gender Filter */}
          <CustomSelect
            value={selectedGender}
            onChange={(value) => setSelectedGender && setSelectedGender(value)}
            options={[
              { value: 'male', label: 'Male (Coming Soon)', disabled: true },
              { value: 'female', label: 'Female' }
            ]}
            className="w-auto min-w-[100px] max-w-[140px]"
          />
          
          {/* Category Filter */}
          <CustomSelect
            value={categoryFilter}
            onChange={(value) => setCategoryFilter(value)}
            options={categories.map((category) => ({
              value: category,
              label: category.charAt(0).toUpperCase() + category.slice(1)
            }))}
            className="w-auto min-w-[80px] max-w-[100px]"
          />
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-500 mb-2"></div>
            <p className="text-sm text-text-description">Loading templates...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-start">
            <i className="fas fa-exclamation-circle text-red-500 mt-0.5 mr-2"></i>
            <div>
              <p className="text-sm font-medium text-red-800">Error loading templates</p>
              <p className="text-xs text-red-600 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Template Grid */}
      {!loading && (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 overflow-y-auto pr-2" style={{ maxHeight }}>
        {filteredTemplates.map((template) => {
          const isSelected = selectedTemplateIds.includes(template.id);
          const hasRandomSelected = selectedTemplateIds.includes('random');
          const hasNonRandomSelected = selectedTemplateIds.some(id => id !== 'random');
          const isDisabled = (template.id === 'random' && hasNonRandomSelected) || (template.id !== 'random' && hasRandomSelected);
          
          return (
            <div
              key={template.id}
              className="w-full"
              style={{ aspectRatio: '9/16' }}
            >
              <button
                onClick={() => !isDisabled && handleTemplateSelect(template)}
                disabled={isDisabled}
                className={`w-full h-full group relative rounded-lg overflow-hidden transition-all duration-200 ${
                  isSelected 
                    ? 'ring-4 ring-secondary-500 ring-opacity-50 shadow-lg' 
                    : 'hover:shadow-md hover:scale-[1.02]'
                }`}
              >
                {template.id === 'random' ? (
                  // Random template - gradient background
                  <div className="w-full h-full bg-gradient-to-br from-secondary-100 to-secondary-200 flex items-center justify-center">
                    <div className="text-center">
                      <i className="fas fa-random text-4xl text-secondary-600 mb-4"></i>
                      <p className="text-lg font-medium text-text-heading">Random</p>
                      <p className="text-sm text-text-description mt-1">Auto Select</p>
                    </div>
                  </div>
                ) : (
                  // Regular template - image
                  <img
                    src={template.imageUrl}
                    alt={`Template ${template.id}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x600?text=Template';
                    }}
                  />
                )}
                <div className={`absolute inset-0 transition-opacity ${
                  isSelected 
                    ? 'bg-secondary-500 bg-opacity-20' 
                    : `bg-black ${isDisabled ? 'bg-opacity-5' : 'bg-opacity-0'} group-hover:bg-opacity-20`
                }`} />
                {isDisabled && (
                  <div className="absolute inset-0 bg-white opacity-40 cursor-not-allowed" />
                )}
                {isSelected && (
                  <div className="absolute top-2 right-2 z-10">
                    <div className="bg-secondary-500 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg border-2 border-white">
                      <i className="fas fa-check text-sm"></i>
                    </div>
                  </div>
                )}
                {template.id !== 'random' && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/70 to-transparent p-3 pt-6">
                    <p className="text-sm font-medium text-white text-center leading-tight mb-0.5">
                      Template ID: {template.id}
                    </p>
                    <p className="text-xs text-white text-center opacity-90">
                      {template.category}
                    </p>
                  </div>
                )}
              </button>
            </div>
          );
        })}
      </div>
      )}

      {/* Selected Templates Bubbles */}
      {selectedTemplateIds.length > 0 && !loading && (
        <div className="mt-3">
          <div className="flex items-center gap-2 mb-2">
            <p className="text-sm font-medium text-text-heading">
              <i className="fas fa-check-circle text-secondary-600 mr-1"></i>
              Selected ({selectedTemplateIds.length})
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedTemplateIds.map((templateId) => {
              const template = templates.find(t => t.id === templateId);
              if (!template) return null;
              
              return (
                <div
                  key={templateId}
                  className="relative group"
                >
                  <div className="relative w-16 h-24 rounded-lg overflow-hidden border-2 border-secondary-500 shadow-md hover:shadow-lg transition-shadow">
                    {template.id === 'random' ? (
                      <div className="w-full h-full bg-gradient-to-br from-secondary-100 to-secondary-200 flex items-center justify-center">
                        <i className="fas fa-random text-2xl text-secondary-600"></i>
                      </div>
                    ) : (
                      <img
                        src={template.imageUrl}
                        alt={template.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x600?text=Template';
                        }}
                      />
                    )}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity" />
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTemplateIds(prev => prev.filter(id => id !== templateId));
                      // Notify parent component about deselection
                      if (onTemplateSelect && template) {
                        onTemplateSelect(template.imageUrl, template.id, template.prompt);
                      }
                    }}
                    className="absolute -top-2 -right-2 flex items-center justify-center w-6 h-6 rounded-full bg-red-500 hover:bg-red-600 text-white shadow-lg transition-colors z-10"
                    title={`Remove ${template.name}`}
                  >
                    <i className="fas fa-times text-xs"></i>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateSelection;

